import { useCallback, useMemo, useState } from 'react';

import { isPrerender } from '@/lib/isPrerender';
import ProductGrid, { ProductLinksScreenReader } from './ProductGrid';
import ProductMarquee3D from './ProductMarquee3D';
import { getHeroProducts } from './heroProducts';

let webglSupport: boolean | null = null;

/** One-shot WebGL probe — the throwaway context is released immediately. */
function supportsWebGL(): boolean {
    if (webglSupport !== null) return webglSupport;
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl2') ?? canvas.getContext('webgl');
        webglSupport = Boolean(gl);
        (gl as WebGLRenderingContext | null)?.getExtension('WEBGL_lose_context')?.loseContext();
    } catch {
        webglSupport = false;
    }
    return webglSupport;
}

function canRender3D(): boolean {
    if (typeof window === 'undefined') return false;
    // The prerender snapshot must capture real markup, not an empty canvas.
    if (isPrerender()) return false;
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return false;
    return supportsWebGL();
}

/**
 * The hero's product showcase. Renders the 3D conveyor where it can, and the static grid everywhere
 * else — reduced motion, no WebGL, or the build-time prerender.
 */
export default function ProductShowcase() {
    const products = useMemo(() => getHeroProducts(), []);
    const [use3D, setUse3D] = useState(canRender3D);
    const handleUnavailable = useCallback(() => setUse3D(false), []);

    if (!use3D) return <ProductGrid products={products} />;

    return (
        <div className="relative -mx-5 aspect-square max-h-full w-[calc(100%+2.5rem)] sm:mx-0 sm:aspect-4/5 sm:w-full sm:max-w-[540px] lg:my-auto lg:mr-0 lg:ml-auto">
            <ProductMarquee3D products={products} onUnavailable={handleUnavailable} />
            <ProductLinksScreenReader products={products} />
        </div>
    );
}
